import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import UploadHistory from "./components/UploadHistory";
import InventorBulkUploadModal from "./InventorBulkUploadModal";
import { useDeferredProfileRefresh } from "../utils/useDeferredProfileRefresh";
import { downloadValidationResult, errorMessageFrom, fetchBatches } from "./inventorImportApi";

/**
 * Past bulk uploads for one client (KOT-109), as a section of the client shell rather than
 * a dialog.
 *
 * A page rather than a modal because the table is nine columns wide: in a dialog it either
 * scrolls sideways - hiding the download button people come here for - or forces the
 * dialog wider than most of the app. A section also gives the view its own URL, so a
 * result can be linked to and the browser's back button behaves.
 *
 * The shell (AddClient) supplies the title and the back arrow for this section, so there
 * is exactly one back control on screen rather than one per level.
 */
const InventorValidationHistory = ({ mode }) => {
  const isView = mode === "view";
  const clientId = useSelector((state) => state.kotClient?.clientId);
  const clientNumber = useSelector((state) => state.kotClient?.clientDetails?.clientNumber);

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [downloadingBatchId, setDownloadingBatchId] = useState(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  // Switching clients dispatches a reset before the new id arrives, so two loads can be in
  // flight at once. Only the newest may write - otherwise a slow response for the previous
  // client overwrites the rows of the one now on screen.
  const requestRef = useRef(0);

  const loadHistory = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const isCurrent = () => requestRef.current === requestId;

    // Mid-switch there is nothing to fetch, and leaving the previous client's uploads on
    // screen would attribute them to the new one.
    if (!clientId) {
      setBatches([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await fetchBatches(clientId);
      if (!isCurrent()) return;
      setBatches(Array.isArray(data) ? data : []);
    } catch (loadError) {
      const message = await errorMessageFrom(loadError, "Failed to load upload history.");
      if (!isCurrent()) return;
      setError(message);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadHistory();
    // Anything still in flight when this tears down belongs to the previous client, or to
    // a component that is no longer mounted.
    return () => {
      requestRef.current += 1;
    };
  }, [loadHistory]);

  /**
   * The imported inventors have to appear in two places: this table, which can refresh
   * straight away because it sits behind the modal, and the client's Inventors grid, which
   * the shell owns and only reloads once the modal is out of the way.
   */
  const { markImported, refreshOnClose } = useDeferredProfileRefresh();

  const handleImported = useCallback(() => {
    markImported();
    loadHistory();
  }, [markImported, loadHistory]);

  const handleUploadClose = useCallback(() => {
    setBulkUploadOpen(false);
    refreshOnClose();
  }, [refreshOnClose]);

  // Kept apart from the load error: they describe different things, and sharing one slot
  // means a download failure hides "could not load the history" over an empty grid, or
  // pins itself above the table with no way to clear it.
  const handleDownloadResult = async (batchId) => {
    setDownloadingBatchId(batchId);
    setDownloadError(null);
    try {
      await downloadValidationResult(batchId);
    } catch (failure) {
      setDownloadError(await errorMessageFrom(failure, "Failed to download the validation result."));
    } finally {
      setDownloadingBatchId(null);
    }
  };

  return (
    <Box p={4} bgcolor="#fff" borderRadius={2} data-testid="inventor-validation-history">
      {/* No heading or back arrow of its own: the client shell titles this section and
          points its single back arrow at Inventors (see utils/sectionHeader.js). */}
      <Box display="flex" justifyContent="flex-end" alignItems="flex-start" gap={2} mb={3}>

        {/* Uploading from here saves walking back to Inventors first - the usual reason to
            open this page is to check a result and then re-upload a corrected file. */}
        {!isView && clientId && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<UploadFileOutlinedIcon />}
            onClick={() => setBulkUploadOpen(true)}
            sx={{ flexShrink: 0 }}
          >
            Bulk Upload
          </Button>
        )}
      </Box>

      <UploadHistory
        batches={batches}
        loading={loading}
        error={error}
        downloadError={downloadError}
        onDownloadResult={handleDownloadResult}
        downloadingBatchId={downloadingBatchId}
      />

      <InventorBulkUploadModal
        open={bulkUploadOpen}
        onClose={handleUploadClose}
        clientProfileId={clientId}
        clientNumber={clientNumber}
        // The new upload has to appear in the list behind the modal.
        onImported={handleImported}
      />
    </Box>
  );
};

export default InventorValidationHistory;
